import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';

import {
  CodeIcon,
  CopyIcon,
  DotMenuIcon,
  ExternalLinkIcon,
} from '@dynatrace/strato-icons';

import { dqlEditorActionsMenuMessages } from './actions-menu-messages.js';
import * as styles from './ActionsMenu.sty.js';
import { Button } from '../../../buttons/button/Button.js';
import { KeyboardShortcut } from '../../../content/keyboard-shortcut/KeyboardShortcut.js';
import { DQL_DOCUMENTATION_URL } from '../../../core/external-links.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import { devLog } from '../../../core/utils/logging.js';
import { isMacPlatform } from '../../../core/utils/user-agent-utils.js';
import { Group } from '../../../navigation/menu/Group.js';
import { Intent } from '../../../navigation/menu/Intent.js';
import { Item } from '../../../navigation/menu/Item.js';
import { Label } from '../../../navigation/menu/Label.js';
import { Link } from '../../../navigation/menu/Link.js';
import { Menu } from '../../../navigation/menu/Menu.js';
import { Sub } from '../../../navigation/menu/Sub.js';

// Menu slot components allowed as children of `DQLEditor.ActionsMenu`
const ALLOWED_MENU_CHILDREN = new Set<unknown>([
  Item,
  Group,
  Label,
  Link,
  Intent,
  Sub,
]);

/**
 * Type guard for transparent wrappers (fragments and divs) whose children are
 * flattened and validated individually.
 */
function isWrapperElement(
  child: ReactElement,
): child is ReactElement<PropsWithChildren> {
  return child.type === Fragment || child.type === 'div';
}

/**
 * Iterates over child nodes and keeps only supported Menu slot components.
 */
function getValidChildren(children: ReactNode): ReactNode[] {
  const validChildren: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    if (isValidElement(child)) {
      if (isWrapperElement(child)) {
        // Flatten fragments and divs so their children are validated individually.
        validChildren.push(...getValidChildren(child.props.children));
        continue;
      }
      if (ALLOWED_MENU_CHILDREN.has(child.type)) {
        validChildren.push(child);
        continue;
      }
    }
    devLog(
      'Only Menu component items are supported as children of DQLEditor.ActionsMenu.',
    );
  }

  return validChildren;
}

/**
 * Slot component that enables the actions menu in the DQLEditor.
 *
 * Rendering this component as a child of `DQLEditor` displays a kebab menu
 * with built-in actions (Copy query, Format query, Open documentation). Custom menu items
 * can be passed as children and will be rendered between the built-in actions.
 * @public
 */
export const ActionsMenu = (props: WithChildren): null => null;
(ActionsMenu as typeof ActionsMenu & { displayName: string }).displayName =
  'DQLEditor.ActionsMenu';

/**
 * Props for the `ActionsMenuInternal` component.
 * @internal
 */
export interface ActionsMenuInternalProps extends PropsWithChildren {
  /**
   * Returns the editor's current content, copied to clipboard when the copy action is triggered.
   */
  getValue: () => string;
  /**
   * The editor's size variant affecting the trigger button and positioning.
   */
  size: 'default' | 'condensed';
  /**
   * When provided, a "Format query" item is shown in the menu.
   */
  onFormat?: () => void;
}

/**
 * Internal component that renders the DQLEditor actions menu.
 * @internal
 */
export const ActionsMenuInternal = ({
  getValue,
  size,
  children,
  onFormat,
}: ActionsMenuInternalProps) => {
  const intl = useIntl();
  // getValue() is called at copy time (not at render time) so it always reads
  // the current editor content from the ref, regardless of re-renders.
  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getValue());
    } catch {
      // For now, we silently ignore clipboard write errors.
    }
  }, [getValue]);

  const validChildren = getValidChildren(children);

  const copyQueryMenuItem = (
    <Menu.Item
      onSelect={() => {
        copyToClipboard().catch(() => void 0);
      }}
      data-dt-children-name="CopyQuery"
    >
      <Menu.Prefix>
        <CopyIcon />
      </Menu.Prefix>
      {intl.formatMessage(dqlEditorActionsMenuMessages.copyQuery)}
    </Menu.Item>
  );

  const openDocumentationMenuItem = (
    <Menu.Link
      href={DQL_DOCUMENTATION_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-dt-children-name="OpenDocumentation"
    >
      <Menu.Prefix>
        <ExternalLinkIcon />
      </Menu.Prefix>
      {intl.formatMessage(dqlEditorActionsMenuMessages.openDocumentation)}
    </Menu.Link>
  );

  const formatQueryMenuItem = onFormat ? (
    <Menu.Item onSelect={onFormat} data-dt-children-name="FormatQuery">
      <Menu.Prefix>
        <CodeIcon />
      </Menu.Prefix>
      {intl.formatMessage(dqlEditorActionsMenuMessages.formatQuery)}
      <Menu.Suffix className={styles.formatQueryShortcut}>
        <KeyboardShortcut
          keys={isMacPlatform() ? 'Cmd+Shift+F' : 'Control+Shift+F'}
          variant="minimal"
        />
      </Menu.Suffix>
    </Menu.Item>
  ) : null;

  return (
    <div className={styles.actionsMenuWrapper({ size })}>
      <Menu>
        <Menu.Trigger>
          <Button
            variant="default"
            size={size}
            aria-label={intl.formatMessage(
              dqlEditorActionsMenuMessages.triggerLabel,
            )}
            data-dt-children-name="OpenActionsMenu"
          >
            <Button.Prefix>
              <DotMenuIcon />
            </Button.Prefix>
          </Button>
        </Menu.Trigger>
        <Menu.Content>
          <Menu.Group>
            {copyQueryMenuItem}
            {formatQueryMenuItem}
            {validChildren}
          </Menu.Group>
          <Menu.Group>{openDocumentationMenuItem}</Menu.Group>
        </Menu.Content>
      </Menu>
    </div>
  );
};
